import { action, computed, persist, thunk } from "easy-peasy";
import API from "API";
import defineAbilitiesFor from "authorization";

export const ACTION_TYPES = {
  SIGNIN_UP: 1,
  SIGNED_UP: 2,
  FAILED: 3,
  FINISHED: 4,
  SIGNIN_IN: 5,
  SIGNED_IN: 6,
  FETCHING_CURRENT_USER: 7,
  FETCHED_CURRENT_USER: 8,
  CONFIRMING_USER: 9,
  CONFIRMED_USER: 10,
  SENDING_PASSWORD_RESET_MAIL: 11,
  SENT_PASSWORD_RESET_MAIL: 12,
  RESETING_PASSWORD: 13,
  RESET_PASSWORD: 14,
  SIGNIN_OUT: 15,
  SIGNED_OUT: 16,
  REFRESHING_TOKEN: 17,
  TOKEN_REFRESHED: 18,
};

const AuthStoreModel = persist(
  {
    authToken: null,
    refreshToken: null,
    isAuthenticated: false,
    process: null,
    error: null,
    user: null,
    abilities: computed((state) => defineAbilitiesFor(state.user)),
    valid: computed((state) => !!state.authToken && state.isAuthenticated),

    setAuthToken: action((state, payload) => {
      state.authToken = payload;
    }),

    setRefreshToken: action((state, payload) => {
      state.refreshToken = payload;
    }),

    setAuthenticated: action((state, payload) => {
      state.isAuthenticated = payload;
    }),

    setProcess: action((state, payload) => {
      state.process = payload;
    }),

    setError: action((state, payload) => {
      state.error = payload;
      state.process = ACTION_TYPES.FAILED;
    }),

    setUser: action((state, payload) => {
      state.user = payload;
    }),

    getCurrentUser: thunk(async (actions, payload, helpers) => {
      actions.setProcess(ACTION_TYPES.FETCHING_CURRENT_USER);
      try {
        const user = await API.get("user/me", payload);
        actions.setProcess(ACTION_TYPES.FETCHED_CURRENT_USER);
        actions.setUser(user);
      } catch (error) {
        console.error("Error while signup -> ", error);
        actions.setError(error);
      }
      actions.setProcess(ACTION_TYPES.FINISHED);
    }),

    signup: thunk(async (actions, payload, helpers) => {
      actions.setProcess(ACTION_TYPES.SIGNIN_UP);
      try {
        await API.post("auth/signup", payload);
        actions.setProcess(ACTION_TYPES.SIGNED_UP);
      } catch (error) {
        console.error("Error while signup -> ", error);
        actions.setError(error);
      }
      actions.setProcess(ACTION_TYPES.FINISHED);
    }),

    login: thunk(async (actions, payload, helpers) => {
      actions.setProcess(ACTION_TYPES.SIGNIN_IN);
      try {
        const tokenData = await API.post("auth/signin", payload);
        actions.setProcess(ACTION_TYPES.SIGNED_IN);
        actions.setAuthToken(tokenData.accessToken);
        actions.setRefreshToken(tokenData.refresh_token);
        actions.setUser(null);
      } catch (error) {
        console.error("Error while login -> ", error);
        actions.setError(error);
      }

      actions.setProcess(ACTION_TYPES.FINISHED);
    }),

    refresh: thunk(async (actions, payload, helpers) => {
      actions.setProcess(ACTION_TYPES.REFRESHING_TOKEN);
      try {
        const tokenData = await API.post("auth/refreshToken", payload);
        actions.setProcess(ACTION_TYPES.TOKEN_REFRESHED);
        actions.setAuthToken(tokenData.accessToken);
        return tokenData;
      } catch (error) {
        console.error("Error while logout -> ", error);
        actions.setAuthToken(null);
        actions.setRefreshToken(null);
        actions.setUser(null);
        actions.setAuthenticated(false);
        actions.setError(error);
      }

      actions.setProcess(ACTION_TYPES.FINISHED);
    }),

    logout: thunk(async (actions, payload, helpers) => {
      actions.setProcess(ACTION_TYPES.SIGNIN_OUT);
      try {
        await API.post("auth/logout", payload);
        actions.setProcess(ACTION_TYPES.SIGED_OUT);
        actions.setAuthToken(null);
        actions.setRefreshToken(null);
        actions.setUser(null);
        actions.setAuthenticated(false);
      } catch (error) {
        console.error("Error while logout -> ", error);
        actions.setError(error);
      }

      actions.setProcess(ACTION_TYPES.FINISHED);
    }),

    confirmEmail: thunk(async (actions, payload, helpers) => {
      actions.setProcess(ACTION_TYPES.CONFIRMING_USER);
      try {
        await API.get("auth/users/" + payload.userId + "/confirmEmail", {
          params: { token: payload.token },
        });
        actions.setProcess(ACTION_TYPES.CONFIRMED_USER);
      } catch (error) {
        console.error("Error while confirmin user -> ", error);
        actions.setError(error);
      }
      actions.setProcess(ACTION_TYPES.FINISHED);
    }),

    forgotPassword: thunk(async (actions, payload, helpers) => {
      actions.setProcess(ACTION_TYPES.SENDING_PASSWORD_RESET_MAIL);
      try {
        await API.get("auth/users/" + payload.email + "/requestPasswordReset");
        actions.setProcess(ACTION_TYPES.SENT_PASSWORD_RESET_MAIL);
      } catch (error) {
        console.error("Error while sending password reset mail -> ", error);
        actions.setError(error);
      }
      actions.setProcess(ACTION_TYPES.FINISHED);
    }),

    resetPassword: thunk(async (actions, payload, helpers) => {
      actions.setProcess(ACTION_TYPES.RESETING_PASSWORD);
      try {
        await API.post(
          "auth/users/" + payload.userId + "/confirmPasswordReset",
          { password: payload.password },
          { params: { token: payload.token } }
        );
        actions.setProcess(ACTION_TYPES.RESET_PASSWORD);
      } catch (error) {
        console.error("Error while reseting password -> ", error);
        actions.setError(error);
      }
      actions.setProcess(ACTION_TYPES.FINISHED);
    }),
  },
  {
    whitelist: ["authToken", "user", "refreshToken"],
    storage: "localStorage",
  }
);

export default AuthStoreModel;
